import { getLang } from '@shared/i18n';
import { Link } from 'react-router-dom';
import { CONTACT_EMAIL, legalUpdated, PUBLISHER } from '../config';

export default function Privacy() {
  return getLang() === 'en' ? <PrivacyEn /> : <PrivacyFr />;
}

function PrivacyEn() {
  return (
    <div className="container narrow legal" style={{ paddingTop: 40 }}>
      <h1>Privacy policy</h1>
      <p className="muted">Last updated: {legalUpdated()}</p>

      <p>
        FollowMe is an app for organising road rallies. This page explains what data is collected, why, who can see it
        and how to exercise your rights. FollowMe shows no advertising, sells no data and uses no advertising tracking
        tools.
      </p>

      <h2>1. Data controller</h2>
      <p>
        {PUBLISHER}, publisher of FollowMe. Contact: <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>.
      </p>

      <h2>2. Data collected</h2>
      <h3>Account</h3>
      <ul>
        <li>e-mail address and password (the password is stored encrypted; we do not know it);</li>
        <li>role (participant, staff, administrator) and account approval status.</li>
      </ul>
      <h3>Profile</h3>
      <ul>
        <li>
          <strong>display name</strong> and <strong>car make / model</strong>: visible to other members (map, chat, list
          of participants);
        </li>
        <li>
          <strong>first name, last name and number plate</strong> (optional): visible only to you and the organisers.
        </li>
      </ul>
      <h3>Taking part in events</h3>
      <ul>
        <li>your registrations (“Join”) and your status: registered, active, took part.</li>
      </ul>
      <h3>GPS location</h3>
      <ul>
        <li>
          collected <strong>only during a rally</strong>, between “Open the map and go” and “Finish”, including when the
          screen is locked or the app is in the background, to guide you along the route;
        </li>
        <li>
          your latest location (with speed, heading and an “off route” indication) is shared live with the event’s
          participants and the organisers;
        </li>
        <li>
          you can hide it from the other participants (Profile → Settings → Privacy); the organisers always see it, for
          safety;
        </li>
        <li>no location is collected outside a rally.</li>
      </ul>
      <h3>Trip history</h3>
      <ul>
        <li>
          at the end of a rally: a simplified track of the trip, distance, durations, average and top speed, share of
          the route followed and distance driven off route. Visible to you and the organisers; you can delete each trip.
        </li>
      </ul>
      <h3>Chat</h3>
      <ul>
        <li>
          messages and photos sent in an event’s chat, visible to its participants and the organisers. Photos are
          downscaled before sending and their location metadata (EXIF) is not transmitted.
        </li>
        <li>
          if you report a message, the organisers see the message, its author and who reported it; the members you block
          are only known to you.
        </li>
      </ul>
      <h3>Notifications</h3>
      <ul>
        <li>
          a technical identifier of the phone (notification token) and the phone’s language, used to send you
          notifications. You choose which ones you receive in Profile → Settings.
        </li>
      </ul>
      <h3>On your phone only</h3>
      <ul>
        <li>
          your preferences (voice guidance: car or phone, speed; map view) and the backup of the trip in progress.
        </li>
      </ul>

      <h2>3. Why, and on what legal basis</h2>
      <ul>
        <li>
          providing the service (account, events, guidance, participants map, chat, history): performance of the service
          you request;
        </li>
        <li>
          location, notifications, camera and photos: only with your permission, which you can withdraw at any time in
          the phone settings;
        </li>
        <li>security of the service (account approval, chat moderation): legitimate interest.</li>
      </ul>

      <h2>4. Service providers</h2>
      <p>Data is processed by the following technical providers, solely to make the app work:</p>
      <ul>
        <li>
          <strong>Supabase</strong>: hosting of the database, accounts and photos;
        </li>
        <li>
          <strong>Cloudflare</strong>: hosting of this website;
        </li>
        <li>
          <strong>Expo (650 Industries)</strong>: delivery of notifications and app updates;
        </li>
        <li>
          <strong>Apple and Google</strong>: delivery of notifications, map display and address search by the phone’s
          operating system;
        </li>
        <li>
          <strong>Valhalla (FOSSGIS, Germany)</strong>, <strong>OpenRouteService (HeiGIT, Germany)</strong> and, as a
          fallback, <strong>OSRM</strong> (routing services): receive coordinates to calculate the route back to the
          track and the guidance instructions, without any identity data;
        </li>
        <li>
          <strong>Photon (komoot) / OpenStreetMap</strong>: searching for a place from the text entered by an organiser,
          and map tiles of the website.
        </li>
      </ul>
      <p>
        Some of these providers may process data outside the European Union; these transfers are covered by the
        safeguards provided for by the GDPR (standard contractual clauses or adequacy decision).
      </p>

      <h2>5. Retention period</h2>
      <ul>
        <li>your data is kept for as long as your account exists;</li>
        <li>the latest shared location for an event is deleted together with the event or with your account;</li>
        <li>
          when the account is deleted, all your data is permanently erased (see{' '}
          <Link to="/suppression-compte">Delete your account</Link>).
        </li>
      </ul>

      <h2>6. Your rights</h2>
      <p>
        You can access your data, correct it (Profile → My account), delete it (account deletion), request its
        portability or object to processing by writing to <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>. You
        can also lodge a complaint with the data protection authority of your country (for example the CNPD in
        Luxembourg, the APD in Belgium or the CNIL in France).
      </p>

      <h2>7. Minors</h2>
      <p>FollowMe is intended for drivers and is not meant for children under 16.</p>

      <h2>8. Changes</h2>
      <p>
        This policy may change along with the app. The date of the last update is shown at the top of the page; in the
        event of a significant change, you will be informed in the app.
      </p>
    </div>
  );
}

function PrivacyFr() {
  return (
    <div className="container narrow legal" style={{ paddingTop: 40 }}>
      <h1>Politique de confidentialité</h1>
      <p className="muted">Dernière mise à jour : {legalUpdated()}</p>

      <p>
        FollowMe est une application d'organisation de rallyes routiers. Cette page explique quelles données sont
        collectées, pourquoi, qui peut les voir et comment exercer vos droits. FollowMe n'affiche aucune publicité, ne
        vend aucune donnée et n'utilise aucun outil de pistage publicitaire.
      </p>

      <h2>1. Responsable du traitement</h2>
      <p>
        {PUBLISHER}, éditeur de FollowMe. Contact : <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>.
      </p>

      <h2>2. Données collectées</h2>
      <h3>Compte</h3>
      <ul>
        <li>adresse e-mail et mot de passe (le mot de passe est stocké chiffré, nous ne le connaissons pas) ;</li>
        <li>rôle (participant, staff, administrateur) et statut de validation du compte.</li>
      </ul>
      <h3>Profil</h3>
      <ul>
        <li>
          <strong>nom affiché</strong> et <strong>marque / modèle de la voiture</strong> : visibles par les autres
          membres (carte, discussion, liste des participants) ;
        </li>
        <li>
          <strong>prénom, nom et plaque d'immatriculation</strong> (facultatifs) : visibles uniquement par vous et les
          organisateurs.
        </li>
      </ul>
      <h3>Participation aux événements</h3>
      <ul>
        <li>vos inscriptions (« Je participe ») et votre statut : inscrit, actif, a participé.</li>
      </ul>
      <h3>Position GPS</h3>
      <ul>
        <li>
          collectée <strong>uniquement pendant un rallye</strong>, entre « Ouvrir la carte et partir » et « Terminer »,
          y compris écran verrouillé ou app en arrière-plan, pour vous guider sur le tracé ;
        </li>
        <li>
          votre dernière position (avec vitesse, direction et indication « hors parcours ») est partagée en direct avec
          les participants de l'événement et les organisateurs ;
        </li>
        <li>
          vous pouvez la masquer aux autres participants (Profil → Réglages → Confidentialité) ; les organisateurs la
          voient toujours, pour la sécurité ;
        </li>
        <li>aucune position n'est collectée en dehors d'un rallye.</li>
      </ul>
      <h3>Historique des trajets</h3>
      <ul>
        <li>
          à la fin d'un rallye : trace simplifiée du trajet, distance, durées, vitesses moyenne et maximale, part du
          tracé suivie et distance roulée hors parcours. Visible par vous et les organisateurs ; vous pouvez supprimer
          chaque trajet.
        </li>
      </ul>
      <h3>Discussion</h3>
      <ul>
        <li>
          messages et photos envoyés dans la discussion d'un événement, visibles par ses participants et les
          organisateurs. Les photos sont réduites avant l'envoi et leurs métadonnées de localisation (EXIF) ne sont pas
          transmises.
        </li>
        <li>
          si vous signalez un message, les organisateurs voient le message, son auteur et qui l'a signalé ; les membres
          que vous bloquez ne sont connus que de vous.
        </li>
      </ul>
      <h3>Notifications</h3>
      <ul>
        <li>
          un identifiant technique du téléphone (jeton de notification) et la langue du téléphone, pour vous envoyer les
          notifications. Vous choisissez lesquelles recevoir dans Profil → Réglages.
        </li>
      </ul>
      <h3>Sur votre téléphone uniquement</h3>
      <ul>
        <li>
          vos préférences (guidage vocal : voiture ou téléphone, vitesse ; vue de la carte) et la sauvegarde du trajet
          en cours.
        </li>
      </ul>

      <h2>3. Pourquoi et sur quelle base</h2>
      <ul>
        <li>
          fournir le service (compte, événements, guidage, carte des participants, discussion, historique) : exécution
          du service que vous demandez ;
        </li>
        <li>
          localisation, notifications, appareil photo et photos : uniquement avec votre autorisation, que vous pouvez
          retirer à tout moment dans les réglages du téléphone ;
        </li>
        <li>sécurité du service (validation des comptes, modération de la discussion) : intérêt légitime.</li>
      </ul>

      <h2>4. Prestataires</h2>
      <p>
        Les données sont traitées par les prestataires techniques suivants, uniquement pour faire fonctionner l'app :
      </p>
      <ul>
        <li>
          <strong>Supabase</strong> : hébergement de la base de données, des comptes et des photos ;
        </li>
        <li>
          <strong>Cloudflare</strong> : hébergement de ce site web ;
        </li>
        <li>
          <strong>Expo (650 Industries)</strong> : envoi des notifications et des mises à jour de l'app ;
        </li>
        <li>
          <strong>Apple et Google</strong> : distribution des notifications, affichage des cartes et recherche
          d'adresses par le système du téléphone ;
        </li>
        <li>
          <strong>Valhalla (FOSSGIS, Allemagne)</strong>, <strong>OpenRouteService (HeiGIT, Allemagne)</strong> et, en
          secours, <strong>OSRM</strong> (services de calcul d'itinéraire) : reçoivent des coordonnées pour calculer
          l'itinéraire de retour au tracé et les instructions de guidage, sans aucune donnée d'identité ;
        </li>
        <li>
          <strong>Photon (komoot) / OpenStreetMap</strong> : recherche d'un lieu à partir du texte saisi par un
          organisateur, et fonds de carte du site web.
        </li>
      </ul>
      <p>
        Certains de ces prestataires peuvent traiter des données hors de l'Union européenne ; ces transferts sont
        encadrés par les garanties prévues par le RGPD (clauses contractuelles types ou décision d'adéquation).
      </p>

      <h2>5. Durée de conservation</h2>
      <ul>
        <li>vos données sont conservées tant que votre compte existe ;</li>
        <li>la dernière position partagée d'un événement est supprimée avec l'événement ou avec votre compte ;</li>
        <li>
          à la suppression du compte, toutes vos données sont effacées définitivement (voir{' '}
          <Link to="/suppression-compte">Supprimer son compte</Link>).
        </li>
      </ul>

      <h2>6. Vos droits</h2>
      <p>
        Vous pouvez accéder à vos données, les corriger (Profil → Mon compte), les supprimer (suppression du compte),
        demander leur portabilité ou vous opposer à un traitement, en écrivant à{' '}
        <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>. Vous pouvez aussi introduire une réclamation auprès de
        l'autorité de protection des données de votre pays (par exemple la CNPD au Luxembourg, l'APD en Belgique ou la
        CNIL en France).
      </p>

      <h2>7. Mineurs</h2>
      <p>FollowMe s'adresse aux conducteurs et n'est pas destinée aux enfants de moins de 16 ans.</p>

      <h2>8. Modifications</h2>
      <p>
        Cette politique peut évoluer avec l'app. La date de dernière mise à jour figure en haut de la page ; en cas de
        changement important, vous en serez informé dans l'app.
      </p>
    </div>
  );
}
